import { useState, useEffect, useRef } from "react";
import { Loader2, FileText, Download, Volume2, Sparkles, Play, Pause } from "lucide-react";
import { BACKEND_URL } from "../../lib/api";
import { VisionAnalysisChip } from "./VisionAnalysisChip";

/**
 * ChatMessage — Wave 19 #85.
 *
 * One message bubble. Knows how to render text, image, video, file and
 * audio (with inline waveform-ish player + transcription footer once
 * Whisper finishes). System bubbles render centred and muted.
 *
 * [Iter 323 — 2026-02-20] Chat images/videos/audio were rendered
 * directly from `msg.upload_url` — a string stored in Mongo at
 * send-time that had `?auth=<jwt>` baked in. When that JWT expired
 * (default TTL was < 30 days) OR the JWT_SECRET rotated, every prior
 * chat image would 401 → broken image icon (user report). Fix:
 * derive the effective media src at RENDER time from `msg.upload_id`
 * plus the current live token, and only fall back to the stored URL
 * when we don't have an id (defensive for very old messages).
 */

function chatMediaSrc(msg) {
  const uid = msg && msg.upload_id;
  if (uid) {
    const tok = (typeof window !== "undefined")
      ? (window.localStorage.getItem("hv_token") || "")
      : "";
    const base = BACKEND_URL || "";
    return `${base}/api/uploads/${uid}${tok ? `?auth=${encodeURIComponent(tok)}` : ""}`;
  }
  // Legacy fallback — a stored URL that may or may not still authenticate.
  return (msg && msg.upload_url) || "";
}

function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}
function fmtDur(s) {
  const x = Math.max(0, Math.round(s || 0));
  return `${Math.floor(x / 60)}:${String(x % 60).padStart(2, "0")}`;
}
function _initials(name) {
  if (!name) return "?";
  const parts = name.split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
}

function _AudioBubble({ msg }) {
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const audioRef = useRef(null);
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setPos(a.currentTime);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, []);
  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); } else { a.pause(); setPlaying(false); }
  };
  const total = msg.upload_duration_s || 0;
  const pct = total > 0 ? Math.min(100, (pos / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[200px]">
      <button
        type="button"
        onClick={toggle}
        className="w-9 h-9 rounded-full bg-[#1fb6c1]/15 border border-[#1fb6c1]/40 flex items-center justify-center text-[#7fe5ec] hover:bg-[#1fb6c1]/25 transition shrink-0"
        data-testid={`chat-audio-play-${msg.id}`}
        aria-label={playing ? "Pause voice note" : "Play voice note"}
        title={playing ? "Pause" : "Play"}
      >
        {/* Iter 397c — was Volume2 in BOTH states → users thought it
            was decorative and never clicked. Distinct Play/Pause
            icons make the affordance obvious. */}
        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-[#1fb6c1] transition-[width] duration-100" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-[10px] text-[#a0aabf] mt-1 tabular-nums">
          {fmtDur(playing ? pos : total)}
        </div>
      </div>
      <audio ref={audioRef} src={chatMediaSrc(msg)} preload="metadata" />
    </div>
  );
}

export default function ChatMessage({ msg, isMine, isFirstInGroup, members, onMediaClick, onActionsRequested, burst }) {
  // Wave 23 — `burst` is an optional array of consecutive same-author
  // image/video messages within ~60 s. When present and length > 1, we
  // render them as a WhatsApp-style 2-col gallery in a single bubble
  // instead of N separate bubbles. The first message in the burst (msg)
  // owns the bubble container, timestamp, and read-receipt.
  const galleryItems = (burst && burst.length > 1) ? burst : null;
  // Wave 19 #94 — long-press / right-click handlers route through to
  // the parent (ChatThreadView) which owns the action menu + dialog.
  // System bubbles also support actions (e.g. open the linked entity).
  const _longPressTimerRef = useRef(null);
  const _longPressFiredRef = useRef(false);
  const _onContextMenu = (e) => {
    if (msg.kind === "system") {
      // System messages with a system_action.link are clickable but not
      // convertible — handled by their own click handler below.
      return;
    }
    if (!onActionsRequested) return;
    e.preventDefault();
    e.stopPropagation();
    onActionsRequested({ msg, x: e.clientX, y: e.clientY });
  };
  const _onTouchStart = (e) => {
    if (msg.kind === "system") return;
    if (!onActionsRequested) return;
    const t = e.touches?.[0];
    const x = t?.clientX || 0;
    const y = t?.clientY || 0;
    _longPressFiredRef.current = false;
    _longPressTimerRef.current = setTimeout(() => {
      _longPressFiredRef.current = true;
      // Wave 19 #99 — clear any text selection iOS/Android may have
      // started on the long-press, so the convert menu isn't covered
      // by the native selection callout.
      try {
        const sel = window.getSelection?.();
        if (sel && typeof sel.removeAllRanges === "function") sel.removeAllRanges();
      } catch {
        /* noop */
      }
      onActionsRequested({ msg, x, y });
    }, 500);
  };
  const _cancelLongPress = () => {
    if (_longPressTimerRef.current) {
      clearTimeout(_longPressTimerRef.current);
      _longPressTimerRef.current = null;
    }
  };
  // If the long-press fired, swallow the click that follows so it
  // doesn't trigger native UI (e.g. media open, link follow).
  const _onTouchEndIntercept = (e) => {
    _cancelLongPress();
    if (_longPressFiredRef.current) {
      try { e.preventDefault(); } catch { /* noop */ }
      _longPressFiredRef.current = false;
    }
  };

  if (msg.kind === "consolidation") {
    return (
      <div
        className="flex justify-center my-3"
        data-testid={`chat-msg-consolidation-${msg.id}`}
      >
        <div className="max-w-[92%] w-full rounded-lg border-2 border-teal-400/50 bg-teal-400/[0.06] p-3">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[10px] uppercase tracking-wider font-bold text-teal-200">Final State</span>
            <span className="text-[10px] text-teal-100 bg-teal-400/20 rounded px-1.5 py-[1px]">
              rev {msg.consolidation_revision ?? "?"}
            </span>
            <span className="text-[10px] text-[#647087] ml-auto">
              locked · report will use this as source of truth
            </span>
          </div>
          <div className="text-[12px] text-[#e5e7eb] leading-relaxed whitespace-pre-wrap">{msg.text}</div>
        </div>
      </div>
    );
  }

  if (msg.kind === "system") {
    const action = msg.system_action;
    const link = action?.link;
    return (
      <div className="flex justify-center my-2" data-testid={`chat-msg-${msg.id}`}>
        {link ? (
          <a
            href={link}
            className="text-[10px] text-[#1fb6c1] bg-[#1fb6c1]/8 border border-[#1fb6c1]/30 hover:bg-[#1fb6c1]/15 hover:text-white transition rounded-full px-3 py-1 max-w-[80%] text-center inline-flex items-center gap-1.5"
            data-testid={`chat-msg-action-${msg.id}`}
          >
            <span>{msg.text}</span>
            <span className="opacity-60">→</span>
          </a>
        ) : (
          <div className="text-[10px] text-[#a0aabf] bg-white/[0.04] border border-white/5 rounded-full px-3 py-1 max-w-[80%] text-center">
            {msg.text}
          </div>
        )}
      </div>
    );
  }
  return (
    <div
      className={`flex ${isMine ? "justify-end" : "justify-start"} ${isFirstInGroup ? "mt-3" : "mt-1"} gap-2.5 select-none`}
      style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" }}
      data-testid={`chat-msg-${msg.id}`}
      onContextMenu={_onContextMenu}
      onTouchStart={_onTouchStart}
      onTouchEnd={_onTouchEndIntercept}
      onTouchMove={_cancelLongPress}
      onTouchCancel={_cancelLongPress}
    >
      {!isMine && (
        // Iter 185 (2026-05-22, Dirk) — Engineering Desk UI now matches
        // the HidroAssistant bubble: gradient Sparkles avatar for the
        // assistant, initials circle for human teammates.
        msg.is_assistant ? (
          <div
            className={`w-8 h-8 rounded-full bg-gradient-to-br from-[#1FB6C1] to-[#A78BFA] text-[#0a0b0e] flex items-center justify-center shrink-0 mt-0.5 shadow-[0_0_12px_rgba(31,182,193,0.25)] ${
              isFirstInGroup ? "" : "invisible"
            }`}
            title={msg.author_name || "Hidro"}
          >
            <Sparkles className="w-3.5 h-3.5" />
          </div>
        ) : (
          <div
            className={`w-8 h-8 rounded-full bg-white/[0.06] border border-white/15 flex items-center justify-center text-[10px] font-bold text-[#cbd2df] shrink-0 mt-0.5 ${
              isFirstInGroup ? "" : "invisible"
            }`}
            title={msg.author_name}
          >
            {_initials(msg.author_name)}
          </div>
        )
      )}
      <div className={`max-w-[85%] ${isMine ? "items-end" : "items-start"} flex flex-col`}>
        {isFirstInGroup && !isMine && !msg.is_assistant && (
          <div className="text-[10px] text-[#a0aabf] mb-0.5 px-1">{msg.author_name}</div>
        )}
        <div
          className={`rounded-2xl px-3.5 py-2.5 text-[15px] sm:text-sm leading-relaxed break-words ${
            isMine
              ? "bg-gradient-to-br from-[#1FB6C1]/20 to-[#1FB6C1]/10 border border-[#1FB6C1]/30 text-white rounded-br-md"
              : "bg-white/[0.04] border border-white/10 rounded-bl-md"
          } ${msg.deleted_at ? "italic opacity-60" : ""}`}
        >
          {msg.deleted_at ? (
            <div
              className="text-xs text-[#a0aabf]"
              data-testid={`chat-msg-tombstone-${msg.id}`}
            >
              Message deleted
            </div>
          ) : (<>
          {msg.kind === "text" && (
            msg.status === "running" && !msg.text ? (
              <div className="flex items-center gap-2 text-xs text-[#a0aabf] py-0.5" data-testid={`chat-thinking-${msg.id}`}>
                <Loader2 className="w-3.5 h-3.5 animate-spin text-[#1FB6C1]" />
                <span>Hidro is thinking…</span>
              </div>
            ) : (
              <div className="whitespace-pre-wrap">
                {msg.text}
                {msg.edited_at && (
                  <span
                    className="ml-2 text-[10px] text-[#a0aabf] italic"
                    title={`Edited at ${msg.edited_at}`}
                    data-testid={`chat-msg-edited-${msg.id}`}
                  >
                    (edited)
                  </span>
                )}
              </div>
            )
          )}
          {/* Wave 23 — gallery render when this msg starts a burst of 2+ media. */}
          {galleryItems && (
            <div
              className={`grid gap-1 ${galleryItems.length === 2 ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-3"}`}
              data-testid={`chat-gallery-${msg.id}`}
              style={{ width: galleryItems.length === 2 ? "min(72vw, 320px)" : "min(78vw, 420px)" }}
            >
              {galleryItems.slice(0, 6).map((g, idx) => {
                const isLast = idx === 5 && galleryItems.length > 6;
                const remaining = galleryItems.length - 6;
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => onMediaClick?.(g)}
                    className="relative aspect-square overflow-hidden rounded bg-black/30 border border-white/10 hover:border-[#1FB6C1]/50 transition"
                    data-testid={`chat-gallery-item-${g.id}`}
                  >
                    {g.kind === "image" ? (
                      <img
                        src={chatMediaSrc(g)}
                        alt={g.upload_filename || "image"}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <>
                        <video
                          src={chatMediaSrc(g)}
                          preload="metadata"
                          className="w-full h-full object-cover pointer-events-none"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-9 h-9 rounded-full bg-black/55 border border-white/30 flex items-center justify-center">
                            <span className="block w-0 h-0 border-l-[10px] border-l-white border-y-[6px] border-y-transparent ml-1" />
                          </div>
                        </div>
                      </>
                    )}
                    {isLast && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-lg font-bold">
                        +{remaining}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {msg.kind === "image" && !galleryItems && (
            <>
              <button
                type="button"
                onClick={() => onMediaClick?.(msg)}
                className="block w-full text-left"
                data-testid={`chat-image-open-${msg.id}`}
              >
                <img
                  src={chatMediaSrc(msg)}
                  alt={msg.upload_filename || "image"}
                  className="max-h-72 rounded object-contain bg-black/30"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    e.currentTarget.parentElement.insertAdjacentHTML(
                      "afterbegin",
                      '<div class="text-xs text-rose-300 italic px-2 py-3">Image failed to load. Tap Download to retry.</div>',
                    );
                  }}
                />
                <div className="text-[10px] text-[#a0aabf] mt-1 truncate">{msg.upload_filename}</div>
              </button>
              <VisionAnalysisChip msg={msg} testIdPrefix="chat-image-vision" />
            </>
          )}
          {msg.kind === "video" && !galleryItems && (
            <button
              type="button"
              onClick={() => onMediaClick?.(msg)}
              className="relative block w-full text-left"
              data-testid={`chat-video-open-${msg.id}`}
            >
              <video
                src={chatMediaSrc(msg)}
                preload="metadata"
                className="max-h-72 rounded bg-black pointer-events-none"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-black/60 border border-white/30 flex items-center justify-center">
                  <span className="block w-0 h-0 border-l-[12px] border-l-white border-y-[8px] border-y-transparent ml-1" />
                </div>
              </div>
              <div className="text-[10px] text-[#a0aabf] mt-1 truncate">{msg.upload_filename}</div>
            </button>
          )}
          {msg.kind === "file" && (
            <div className="flex flex-col gap-1">
              <a
                href={chatMediaSrc(msg)}
                download={msg.upload_filename || "download"}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm hover:underline"
                data-testid={`chat-file-${msg.id}`}
              >
                <FileText className="w-4 h-4 shrink-0" />
                <span className="truncate">{msg.upload_filename || "Download"}</span>
                <Download className="w-3 h-3 shrink-0 opacity-60" />
              </a>
              {/* Iter 427.p2b — Extraction status chip so the operator
                  knows exactly whether the AI can read this attachment.
                  Prevents the "why won't it read my PDF?" cycle by
                  making success/failure visible on the bubble. */}
              {msg.llm_excerpt_status && (
                <div
                  className={`text-[10px] flex items-center gap-1 ${
                    msg.llm_excerpt_status === "ready"
                      ? "text-emerald-300"
                      : msg.llm_excerpt_status === "running"
                        ? "text-amber-300"
                        : msg.llm_excerpt_status === "empty"
                          ? "text-orange-300"
                          : msg.llm_excerpt_status === "error"
                            ? "text-red-300"
                            : "text-[#647087]"
                  }`}
                  data-testid={`chat-file-ocr-status-${msg.id}`}
                >
                  {msg.llm_excerpt_status === "running" && (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>OCR extracting…</span>
                    </>
                  )}
                  {msg.llm_excerpt_status === "ready" && (
                    <span>
                      OCR ready · Hidro will read{" "}
                      {msg.llm_excerpt ? `${(msg.llm_excerpt.length / 1000).toFixed(1)}k chars` : "the content"}
                    </span>
                  )}
                  {msg.llm_excerpt_status === "empty" && (
                    <span>OCR found no text · paste key values manually</span>
                  )}
                  {msg.llm_excerpt_status === "error" && (
                    <span>OCR failed · re-attach or paste key values manually</span>
                  )}
                </div>
              )}
            </div>
          )}
          {msg.kind === "audio" && <_AudioBubble msg={msg} />}
          </>)}
        </div>
        {/* Transcription footer for voice notes */}
        {msg.kind === "audio" && (
          <div className="text-xs italic text-[#a0aabf] mt-1 px-2 max-w-full">
            {msg.transcription_status === "pending" && (
              <span className="inline-flex items-center gap-1.5">
                <Loader2 className="w-3 h-3 animate-spin" /> Transcribing…
              </span>
            )}
            {msg.transcription_status === "done" && msg.transcription && (
              <span className="inline-flex items-start gap-1.5 group/transcript max-w-full">
                <span
                  className="text-[#dde3ec] select-text whitespace-pre-wrap break-words"
                  data-testid={`audio-transcription-${msg.id}`}
                >
                  &ldquo;{msg.transcription}&rdquo;
                </span>
                <button
                  type="button"
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(msg.transcription || "");
                    } catch (_e) { /* clipboard may be unavailable */ }
                  }}
                  className="opacity-0 group-hover/transcript:opacity-100 transition shrink-0 mt-0.5 text-[#647087] hover:text-cyan-300 not-italic"
                  title="Copy transcription"
                  aria-label="Copy transcription"
                  data-testid={`audio-transcription-copy-${msg.id}`}
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
              </span>
            )}
            {msg.transcription_status === "failed" && (
              <span className="inline-flex items-center gap-2 not-italic">
                <span className="text-rose-300 italic">Transcription failed</span>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const api = (await import("../../lib/api")).default;
                      await api.post(`/chats/${msg.thread_id}/messages/${msg.id}/retranscribe`);
                    } catch (_e) { /* toast handled by http interceptor */ }
                  }}
                  className="text-[10px] text-cyan-300 hover:text-cyan-200 underline decoration-dotted"
                  data-testid={`audio-transcription-retry-${msg.id}`}
                  title="Retry Whisper transcription"
                >
                  Retry
                </button>
              </span>
            )}
          </div>
        )}
        <div className="text-[9px] text-[#647087] mt-0.5 px-1 tabular-nums flex items-center gap-1">
          <span>{fmtTime(msg.created_at)}</span>
          {isMine && msg.read_by && msg.read_by.length > 1 && (
            <span
              className="text-emerald-300"
              title={(() => {
                const others = (msg.read_by || []).filter((id) => id !== msg.author_id);
                const names = (members || [])
                  .filter((m) => others.includes(m.id))
                  .map((m) => m.name)
                  .filter(Boolean);
                return names.length ? `Read by ${names.join(", ")}` : "Read";
              })()}
              data-testid={`chat-read-${msg.id}`}
            >
              ✓✓
            </span>
          )}
          {isMine && (!msg.read_by || msg.read_by.length <= 1) && (
            <span className="text-[#647087]" title="Sent">✓</span>
          )}
        </div>
      </div>
    </div>
  );
}
